import { ComponentProps, type ForwardedRef, forwardRef, ReactNode, useId } from 'react';
import { useFormContext } from 'react-hook-form';
import clsx from 'clsx';

import styles from './Input.module.css';

interface CustomInputProps extends Omit<ComponentProps<'input'>, 'prefix' | 'suffix'> {
  prefix?: ReactNode;
  suffix?: ReactNode;
  name: string;
  label?: ReactNode;
  required?: boolean;
  error?: string;
}

const Input = forwardRef<HTMLInputElement, CustomInputProps>(function CustomInput(
  { prefix, suffix, label, error, required, className, disabled, name, ...props },
  ref,
) {
  const { register } = useFormContext();
  const inputProps = register(name);

  const id = useId();

  return (
    <div className={clsx(styles.container, className)}>
      {label && (
        <label
          className={styles.label}
          id={`${id}-label`}
          htmlFor={id}
        >
          {label} {required ? '*' : ''}
        </label>
      )}
      <div className={clsx(styles.wrapper, error && styles.error, disabled && styles.disabled)}>
        {prefix && <div className={styles.prefix}>{prefix}</div>}
        <input
          {...props}
          {...inputProps}
          ref={(element) => {
            inputProps.ref(element);
            assignRef(ref, element);
          }}
          id={id}
          className={styles.input}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          disabled={disabled}
        />
        {suffix && <div className={styles.suffix}>{suffix}</div>}
      </div>
      {error && (
        <span
          id={`${id}-error`}
          className={styles.errorLabel}
        >
          {error}
        </span>
      )}
    </div>
  );
});

export default Input;

function assignRef<T>(ref: ForwardedRef<T>, value: T) {
  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  if (ref) {
    ref.current = value;
  }
}
